import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useFragment } from 'react-relay';
import { graphql } from 'babel-plugin-relay/macro';
import ReactHtmlParser from 'react-html-parser';
import { format } from 'date-fns';

import usePageTitle from '../../hooks/usePageTitle';

import { transformSource } from '../../utils/helpers';
import { DICTIONARIES } from '../../utils/constants';

import type { AboutQuery$key } from './__generated__/AboutQuery.graphql';

import './About.sass';

import ImageSlider from '../../components/ImageSlider';
import Statistics from '../../components/Statistics';
import AboutCompany from '../../components/AboutCompany';
import BrandMaterials from '../../components/BrandMaterials';
import FileLink from '../../uikit/FileLink';

interface Props {
	query: AboutQuery$key;
	labels: {
		[keys: string]: string;
	};
}

function About(props: Props) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data: any = useFragment(
		graphql`
			fragment AboutQuery on HomePublishedContent {
				_children {
					items {
						... on AboutPublishedContent {
							brandPacks {
								... on BrandPackPublishedContent {
									brandTitle
									darkMode
									logoPreview {
										_url
										_name
									}
									sVGResource {
										_name
										_url
									}
									pNGResource {
										_name
										_url
									}
									ePSResource {
										_name
										_url
									}
								}
							}
							metaTitle
							headerTilte
							headerText
							photoCarousel {
								... on CarouselItemPicturePublishedContent {
									picture
									fullImage
								}
							}
							infoItems {
								... on InfoItemPublishedContent {
									text
									value
								}
							}
							contentText
							contentTitle
							conferenceFiles {
								_id
								_name
								_url
								... on FilePublishedMedia {
									umbracoBytes
									_updateDate
								}
							}
						}
					}
				}
			}
		`,
		props.query
	);

	const [fetchedData, setFetchedData] = useState<{
		headerTilte: string;
		headerText: string;
		contentTitle: string;
		contentText: string;
		metaTitle: string;
		infoItems: [];
		photoCarousel: [];
		conferenceFiles: {
			_id: string;
			_name: string;
			_url: string;
			umbracoBytes: number;
			_updateDate: number | Date;
		}[];
		brandPacks: [];
	}>();
	useEffect(() => {
		data._children.items.map(
			(item: {
				headerTilte: string;
				headerText: string;
				contentTitle: string;
				contentText: string;
				metaTitle: string;
				infoItems: [];
				photoCarousel: [];
				conferenceFiles: {
					_id: string;
					_name: string;
					_url: string;
					umbracoBytes: number;
					_updateDate: number | Date;
				}[];
				brandPacks: [];
			}) => {
				if (item && Object.keys(item).length !== 0) {
					setFetchedData(item);
				}
			}
		);
	}, [data]);

	usePageTitle(fetchedData?.metaTitle);

	return (
		<div className="about-page">
			<section className="about-top">
				<Container>
					<Row>
						<Col xs={12} lg={6}>
							{fetchedData && (
								<h1 className="uikit-heading-hg about-top__heading">
									{fetchedData.headerTilte}
								</h1>
							)}
							{fetchedData &&
								ReactHtmlParser(fetchedData.headerText, {
									transform: transformSource,
								})}
						</Col>
						<Col
							xs={12}
							lg={{ offset: 1, span: 5 }}
							className="about-top__carousel"
						>
							{fetchedData && (
								<ImageSlider photos={fetchedData.photoCarousel} />
							)}
						</Col>
					</Row>
				</Container>
			</section>
			{fetchedData && (
				<div className="about-statistics">
					<Statistics statistic={fetchedData.infoItems} />
				</div>
			)}
			{fetchedData && (
				<section className="about-dataart">
					<AboutCompany
						sectionTitle={props.labels[DICTIONARIES.aboutDataArtTitle]}
						title={fetchedData.contentTitle}
						text={fetchedData.contentText}
					/>
				</section>
			)}
			{fetchedData && (
				<div className="about-brand-materials">
					<BrandMaterials
						sectionTitle={props.labels[DICTIONARIES.brandsTitle]}
						brands={fetchedData.brandPacks}
						legalNotice={props.labels[DICTIONARIES.legalNotice]}
					/>
				</div>
			)}
			<Container className="about-file-link">
				{fetchedData?.conferenceFiles && (
					<Row>
						<Col xs={12} md={10} lg={8}>
							{fetchedData?.conferenceFiles
								.filter((i: any) => i._url.includes('.pdf'))
								.map((file, i) => (
									<FileLink
										key={file._id}
										fileName={file._name}
										fileUrl={file._url}
										size={file.umbracoBytes}
										updateDate={format(new Date(file._updateDate), 'MM/yyyy')}
									/>
								))}
						</Col>
					</Row>
				)}
			</Container>
		</div>
	);
}

export default About;
